import React, { useState, useRef, useEffect } from 'react';
import InstallAppButton from './InstallAppButton';
import NotificationMenuItem from './NotificationMenuItem';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAdmin } from '../hooks/useAdmin';
import { useTheme } from '../context/ThemeContext';
import { useT, LANGS } from '../i18n';
import { generateShareCode } from '../firebase/shareCodeService';
import PersonIcon from './PersonIcon';
import './UserMenu.css';

const CLOSE_ANIM_MS = 150;

function GearIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.65 1.65 0 004.6 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 4.6a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
  );
}

// Drop-in authenticated-nav user block: avatar/name (→ Settings) + a gear icon
// beside it that opens a dropdown with account settings, language, dark/light
// mode, and sign out — self-contained (useAuth/useTheme/useT internally), so
// it slots into Dashboard/Settings/Workspace's differently-styled navs with
// no page-specific wiring beyond dropping it in.
export default function UserMenu() {
  const { user, profile, logout } = useAuth();
  // `force: false` — this menu is in the header of every page, so it reads the
  // CACHED token rather than forcing a refresh per page load for every user.
  // See the note in useAdmin; AdminRoute does the authoritative check.
  const { isAdmin } = useAdmin({ force: false });
  const { theme, toggleTheme } = useTheme();
  const { t, lang, setLang } = useT();
  const navigate = useNavigate();

  // `open` drives the animated in/out state (opacity+transform); `rendered`
  // keeps the dropdown mounted for the brief close animation before it's
  // actually removed from the DOM — a plain `{open && <div/>}` can't animate
  // its own removal, since the element is just gone the instant open flips.
  const [open, setOpen] = useState(false);
  const [rendered, setRendered] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [shareCode, setShareCode] = useState('');
  const [genBusy, setGenBusy] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const menuRef = useRef(null);
  const closeTimerRef = useRef(null);

  // profile.fullName first: it is the value the designer profile edits, and it
  // refreshes immediately after a save, whereas Auth's displayName lags a tick.
  const displayName = profile?.fullName || user?.displayName || user?.email?.split('@')[0] || 'Designer';
  // Designers upload a profile picture; everyone else gets the person icon.
  const avatarUrl = profile?.avatarUrl || '';
  const isDark = theme === 'dark';

  function openMenu() {
    clearTimeout(closeTimerRef.current);
    setRendered(true);
    // Two rAFs: the first commits the "just mounted" (closed) styles, the
    // second flips to "open" on the following frame so the browser actually
    // transitions between two different states instead of skipping straight
    // to the end (which is what happens if both are set in the same paint).
    requestAnimationFrame(() => requestAnimationFrame(() => setOpen(true)));
  }
  function closeMenu() {
    setOpen(false);
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setRendered(false), CLOSE_ANIM_MS);
  }

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  // Close on any click/tap outside the menu, and on Escape. A document-level
  // listener (rather than a full-screen overlay) — an overlay positioned
  // `fixed` would only span the nearest ancestor that establishes its own
  // containing block, and this nav's `backdrop-filter: blur(...)` does
  // exactly that, so a fixed overlay here silently only covered the header,
  // never the page body below it.
  useEffect(() => {
    if (!rendered) return;
    function onPointerDown(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) closeMenu();
    }
    function onKey(e) { if (e.key === 'Escape') closeMenu(); }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [rendered]);

  async function handleLogout() {
    closeMenu();
    setLoggingOut(true);
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch {
      setLoggingOut(false);
    }
  }

  async function handleGenerateCode() {
    if (!user?.uid || genBusy) return;
    setGenBusy(true);
    setCodeCopied(false);
    try {
      const code = await generateShareCode(user.uid);
      setShareCode(code);
    } catch (err) {
      console.error('[ShareCode] generate failed:', err);
    } finally {
      setGenBusy(false);
    }
  }

  async function copyCode() {
    try { await navigator.clipboard.writeText(shareCode); }
    catch {
      const ta = document.createElement('textarea');
      ta.value = shareCode; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 1600);
  }

  const chipStyle = {
    display: 'flex', alignItems: 'center', gap: 9,
    padding: '4px 12px 4px 4px',
    background: 'var(--site-card-bg)',
    border: '1px solid var(--site-border-2)',
    borderRadius: 99,
    cursor: 'pointer',
    boxShadow: '0 1px 3px var(--site-shadow)',
    fontFamily: 'inherit',
    textAlign: 'left',
  };

  const avatarStyle = {
    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
    background: 'var(--avatar-fallback)',
    color: '#fff', fontSize: '0.63rem', fontWeight: 800, letterSpacing: '0.04em',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  // Fills the circle without distortion whatever aspect the source had.
  const avatarImgStyle = {
    width: '100%', height: '100%', objectFit: 'cover',
    borderRadius: '50%', display: 'block',
  };

  const itemStyle = {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
    padding: '11px 14px', background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '0.83rem', fontWeight: 600, color: 'var(--site-text-1)', fontFamily: 'inherit',
    textAlign: 'left',
  };

  const dividerStyle = { height: 1, background: 'var(--site-border)' };

  return (
    // Layout in CSS rather than inline, so a stylesheet can hide the whole
    // control on phones (see MobileTabBar.css). An inline `display: flex` beats
    // any rule short of `!important`, which is not a fight worth having.
    <div ref={menuRef} className="um-root">
      {/* YOUR NAME AND PICTURE GO TO YOUR PROFILE, not to settings. A portrait
          and a name are the universal affordance for "you" — settings is what
          the gear beside it is for, and it is still the first item in the
          dropdown. Until the profile page existed there was nowhere else for
          this to lead; now there is, and a customer had no other route to it
          on a desktop at all.

          Hidden on phones (see styles/mobile.css) — the name and email eat
          most of a narrow header, and the tab bar carries a profile tab. */}
      <button type="button" className="um-chip" style={chipStyle} onClick={() => navigate('/profile')} title={t('tab_profile')}>
        <div style={avatarStyle}>
          {avatarUrl
            ? <img src={avatarUrl} alt="" style={avatarImgStyle} />
            : <PersonIcon />}
        </div>
        {/* PICTURE AND NAME. The email used to sit under it — an address you
            already know, in a header that has to hold a search box, a bell and
            a messages button beside it, and it was the widest thing here. It
            is still on the profile page this chip now opens. */}
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--site-text-1)', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
          {displayName}
        </span>
      </button>

      {/* Gear icon — right of the name — opens the dropdown. The class exists
          only so the touch sizing in styles/mobile.css can keep it SQUARE; a
          50% radius on a 32×44 box draws an ellipse. */}
      <button
        type="button"
        className="um-gear"
        onClick={() => (open ? closeMenu() : openMenu())}
        aria-label={t('set_title')}
        title={t('set_title')}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
          border: `1px solid ${open ? '#6366F1' : 'var(--site-border-2)'}`,
          background: open ? 'rgba(99,102,241,0.1)' : 'var(--site-card-bg)',
          color: open ? '#6366F1' : 'var(--site-text-2)',
          cursor: 'pointer',
          boxShadow: '0 1px 3px var(--site-shadow)',
          transition: 'all 0.15s',
        }}
      >
        <GearIcon />
      </button>

      {rendered && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 40,
          width: 230, borderRadius: 14, overflow: 'hidden',
          background: 'var(--site-card-bg)',
          border: '1px solid var(--site-border-2)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.18), 0 4px 12px var(--site-shadow)',
          transformOrigin: 'top right',
          opacity: open ? 1 : 0,
          transform: open ? 'translateY(0) scale(1)' : 'translateY(-6px) scale(0.96)',
          transition: `opacity ${CLOSE_ANIM_MS}ms ease, transform ${CLOSE_ANIM_MS}ms ease`,
          pointerEvents: open ? 'auto' : 'none',
        }}>
          {/* ── ADMIN PANEL ───────────────────────────────────────────────────
              Rendered ONLY for an account whose ID token carries the `admin`
              claim — not hidden with CSS, not disabled: absent from the DOM
              entirely, so it cannot be found by reading the page.

              THIS IS NOT WHAT MAKES /admin SECURE, and it is important not to
              mistake it for that. A determined visitor can navigate straight to
              the route whatever this menu shows. What actually stops them is
              server-side and unaffected by anything in the browser:
                · firestore.rules refuses stats/, statsDaily/ and activity/ to
                  any token without `admin`;
                · every admin callable re-checks the claim before it answers;
                · the claim itself can only be set with the Admin SDK, off the
                  client — see functions/adminClaims.js.
              So this entry exists to stop ordinary users meeting a page full of
              permission errors, and to keep the admin area from advertising
              itself. It is a signpost, not a lock.

              First, and visually separated, because it leaves the app for a
              different tool rather than changing something about this one. */}
          {isAdmin && (
            <>
              <button
                type="button"
                onClick={() => { closeMenu(); navigate('/admin'); }}
                style={{ ...itemStyle, color: 'var(--site-text-1)', fontWeight: 700 }}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                  strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 22s8-4 8-10V5.5L12 2 4 5.5V12c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
                {t('menu_admin')}
              </button>
              <div style={dividerStyle} />
            </>
          )}

          {/* Settings */}
          <button type="button" onClick={() => { closeMenu(); navigate('/settings'); }} style={itemStyle}>
            <GearIcon />
            {t('set_title')}
          </button>

          {/* ── PLANS & CREDITS ───────────────────────────────────────────────
              The header's credits badge already links here, but it reads as a
              BALANCE — a number tells you how many you have, not that there is
              somewhere to go and get more. This is the named route to the same
              page, in the menu people open when they are looking for one.

              NOT DESIGNER-ONLY, deliberately. The tier ladder is for designers,
              but the page's other half is credit packs, and a customer spends
              those on AI staging exactly as a designer does — Plans.jsx already
              branches to a customer view for precisely that reason. Gating this
              entry on `userType` would leave a homeowner who runs out of credits
              with the badge as their only route to buying more.

              Beside Settings rather than in its own group: both are "your
              account", and the divider below already separates them from the
              share-code tools.

              The sparkle is the glyph the credits badge uses for this same
              destination. One idea, one icon. */}
          <button type="button" onClick={() => { closeMenu(); navigate('/plans'); }} style={itemStyle}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10 3.5 11.7 8 16.2 9.7 11.7 11.4 10 15.9 8.3 11.4 3.8 9.7 8.3 8Z" />
              <path d="M17.5 14.2 18.4 16.4 20.6 17.3 18.4 18.2 17.5 20.4 16.6 18.2 14.4 17.3 16.6 16.4Z" />
            </svg>
            {t('menu_plans')}
          </button>

          <div style={dividerStyle} />

          {/* Generate share code — hand it to a design owner so they can grant
              you edit access to their shared project. */}
          <button type="button" onClick={handleGenerateCode} disabled={genBusy} style={{ ...itemStyle, cursor: genBusy ? 'default' : 'pointer', opacity: genBusy ? 0.6 : 1 }}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 7h3a2 2 0 012 2v9a2 2 0 01-2 2H9a2 2 0 01-2-2v-3"/>
              <path d="M12 2v10m0 0l3-3m-3 3l-3-3"/>
            </svg>
            {genBusy ? t('menu_generating') : t('menu_gen_code')}
          </button>

          {shareCode && (
            <div style={{ padding: '2px 14px 12px' }}>
              <div style={{ fontSize: '0.62rem', color: 'var(--site-text-3)', marginBottom: 6, lineHeight: 1.4 }}>
                {t('menu_code_hint')}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <code style={{
                  flex: 1, minWidth: 0, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.02em',
                  padding: '7px 9px', borderRadius: 8, background: 'var(--site-bg)',
                  border: '1px solid var(--site-border)', color: 'var(--site-text-1)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace',
                }}>{shareCode}</code>
                <button type="button" onClick={copyCode} style={{
                  flexShrink: 0, padding: '7px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontSize: '0.72rem', fontWeight: 700, fontFamily: 'inherit',
                  background: '#6366F1', color: '#fff',
                }}>{codeCopied ? t('share_copied') : t('share_copy')}</button>
              </div>
            </div>
          )}

          <div style={dividerStyle} />

          {/* Language */}
          <div style={{ padding: '12px 14px 10px' }}>
            <div style={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--site-text-3)', marginBottom: 8 }}>
              {t('menu_language')}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {LANGS.map(l => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => { setLang(l.code); closeMenu(); }}
                  style={{
                    flex: 1, padding: '6px 0', borderRadius: 8, fontSize: '0.76rem', fontWeight: 700,
                    fontFamily: 'inherit', cursor: 'pointer',
                    border: lang === l.code ? '1px solid rgba(99,102,241,0.35)' : '1px solid var(--site-border)',
                    background: lang === l.code ? 'rgba(99,102,241,0.1)' : 'transparent',
                    color: lang === l.code ? '#6366F1' : 'var(--site-text-2)',
                  }}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div style={dividerStyle} />

          {/* Install as an app. Renders nothing when the app is already
              installed or the browser cannot install at all, so the divider
              above it is not left orphaned by an empty row. */}
          <InstallAppButton style={itemStyle} onDone={closeMenu} />

          {/* Push notifications. Deliberately does NOT close the menu — the
              switch is the feedback, and a dropdown that vanishes the instant
              you flip it hides whether it actually turned on. */}
          <NotificationMenuItem style={itemStyle} />

          {/* Dark / light mode */}
          <button type="button" onClick={() => { toggleTheme(); closeMenu(); }} style={itemStyle}>
            {isDark ? (
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
              </svg>
            )}
            {isDark ? t('menu_light_mode') : t('menu_dark_mode')}
          </button>

          <div style={dividerStyle} />

          {/* Sign out */}
          <button
            type="button"
            onClick={handleLogout}
            disabled={loggingOut}
            style={{ ...itemStyle, color: '#DC2626', cursor: loggingOut ? 'not-allowed' : 'pointer', opacity: loggingOut ? 0.6 : 1 }}
          >
            {loggingOut ? (
              <span style={{
                width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                border: '2px solid rgba(220,38,38,0.25)', borderTopColor: '#DC2626',
                animation: 'user-menu-spin 0.7s linear infinite',
              }} />
            ) : (
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            )}
            {loggingOut ? t('dash_signing_out') : t('menu_sign_out')}
          </button>
        </div>
      )}

      <style>{'@keyframes user-menu-spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}
